// The Marketplace: buy and sell items with other real players, at a fixed price or by auction.
import { useEffect, useState } from 'react';
import { api } from '../api.ts';
import { useGame, money, fmt, duration, ago } from '../state.tsx';
import { Loading, useLoad, Panel, PlayerLink, ItemIcon, ItemName } from '../components/ui.tsx';

const CATS: [string, string][] = [['all', 'All'], ['weapon', 'Weapons'], ['armor', 'Armor'], ['vehicle', 'Vehicles'], ['supply', 'Supplies'], ['collectible', 'Collectibles'], ['consumable', 'Usable']];

let pendingSell: string | undefined;
/** Open the Marketplace with the sell form ready for this item. */
export function sellOnMarket(itemId: string) {
  pendingSell = itemId;
  window.location.hash = '#/inventory/market';
}

export function MarketTab({ owned, reloadInventory }: { owned: any[]; reloadInventory: () => void }) {
  const [preselect] = useState(() => {
    const p = pendingSell;
    pendingSell = undefined;
    return p;
  });
  const { me, act, byId, setFlash } = useGame();
  const { data, reload } = useLoad(() => api.get('/api/market'), [me?.cash], 30_000);
  const [cat, setCat] = useState('all');
  const [q, setQ] = useState('');
  const [bids, setBids] = useState<Record<number, string>>({});
  if (!data) return <Loading />;
  const refresh = () => {
    reload();
    reloadInventory();
  };
  const buy = async (l: any) => {
    if (!confirm(`Buy ${l.qty > 1 ? `${l.qty} × ` : ''}${byId.items.get(l.itemId).name} for ${money(l.price)}?`)) return;
    const r = await act('/api/market/buy', { listingId: l.id });
    if (r) setFlash({ kind: 'good', text: <>You bought <ItemName itemId={l.itemId} /> for <span className="money">{money(r.price)}</span>.</> });
    refresh();
  };
  const bid = async (l: any) => {
    const amount = Math.floor(Number((bids[l.id] ?? String(l.minBid)).replace(/[^0-9]/g, '')));
    const r = await act('/api/market/bid', { listingId: l.id, amount });
    if (r) setFlash({ kind: 'good', text: <>You’re the top bidder at <span className="money">{money(r.bid)}</span>. The money is held until the auction ends (refunded if someone outbids you).</> });
    refresh();
  };
  const cancel = async (l: any) => {
    await act('/api/market/cancel', { listingId: l.id });
    refresh();
  };
  const term = q.trim().toLowerCase();
  const shown = data.active.filter((l: any) => {
    const item = byId.items.get(l.itemId);
    return (cat === 'all' || item?.category === cat) && (!term || item?.name.toLowerCase().includes(term));
  });

  return (
    <div>
      <p className="muted">
        Trade items with other real players. <b>Buy Now</b> listings sell instantly; <b>auctions</b> go to the highest bidder when the timer runs out
        (a bid in the last couple of minutes adds a little time). Bids are paid up front and refunded if you’re outbid. The market keeps {data.feePct}% of each sale.
        Listed items are held by the market until they sell; unsold ones come back to you.
      </p>
      <SellForm owned={owned} preselect={preselect} feePct={data.feePct} onDone={refresh} />
      <div className="subnav" style={{ margin: '8px 0', background: '#000' }}>
        {CATS.map(([c, label]) => <a key={c} className={cat === c ? 'active' : ''} onClick={() => setCat(c)}>{label}</a>)}
      </div>
      <div className="row" style={{ marginBottom: 6 }}>
        <input className="grow" placeholder="Search items…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {shown.length === 0 ? <div className="muted">Nothing for sale{cat !== 'all' || term ? ' matching that' : ' right now'}.</div> : (
        <table className="list stack market-table">
          <thead><tr><th></th><th>Item</th><th>Seller</th><th>Price</th><th>Ends</th><th></th></tr></thead>
          <tbody>
            {shown.map((l: any) => {
              const item = byId.items.get(l.itemId);
              const mine = l.sellerId === me.id;
              return (
                <tr key={l.id}>
                  <td style={{ width: 46 }}><ItemIcon itemId={l.itemId} size={40} /></td>
                  <td>
                    <ItemName itemId={l.itemId} />{l.qty > 1 && <b> ×{l.qty}</b>}
                    {item.category !== 'collectible' && item.category !== 'supply' && item.category !== 'consumable' && <div className="small dim">Attack {item.attack} · Defense {item.defense}</div>}
                    <div className="small dim">{l.kind === 'auction' ? `Auction · ${l.bids} bid${l.bids === 1 ? '' : 's'}` : 'Buy Now'}</div>
                  </td>
                  <td><PlayerLink id={l.sellerId} name={l.sellerName} /></td>
                  <td>
                    {l.kind === 'auction' ? (
                      <>
                        <div className="money">{money(l.topBid ?? l.price)}</div>
                        <div className="small dim">{l.topBid ? <>top: {l.topBidderId === me.id ? <b className="good">you</b> : l.topBidderName}</> : 'starting bid'}</div>
                      </>
                    ) : <div className="money">{money(l.price)}</div>}
                  </td>
                  <td className="small">{duration(Math.max(0, l.endsAt - Date.now()))}</td>
                  <td className="right" style={{ whiteSpace: 'nowrap' }}>
                    {mine ? (l.bids === 0 ? <a className="small" onClick={() => cancel(l)}>take it down</a> : <span className="small dim">your auction</span>)
                      : l.kind === 'fixed' ? <button className={`btn ${me.cash >= l.price ? '' : 'disabled'}`} onClick={() => buy(l)}>Buy</button>
                      : l.topBidderId === me.id ? <span className="small good">You’re winning</span>
                      : (
                        <span className="bid-box">
                          $<input value={bids[l.id] ?? String(l.minBid)} onChange={(e) => setBids({ ...bids, [l.id]: e.target.value })} style={{ width: 80 }} />
                          <button className="btn small" onClick={() => bid(l)}>Bid</button>
                        </span>
                      )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {data.mine.length > 0 && (
        <Panel title="Your listings">
          {data.mine.map((l: any) => (
            <div key={l.id} className="feed-item">
              <ItemIcon itemId={l.itemId} size={24} />
              <span className="grow"><ItemName itemId={l.itemId} />{l.qty > 1 ? ` ×${l.qty}` : ''} · {l.kind === 'auction' ? 'auction' : 'buy now'}</span>
              <span className="small">
                {l.status === 'active' ? <>{l.kind === 'auction' ? (l.topBid ? `top bid ${money(l.topBid)}` : 'no bids yet') : money(l.price)} · ends in {duration(Math.max(0, l.endsAt - Date.now()))}</>
                  : l.status === 'sold' ? <span className="good">sold for {money(l.soldPrice)}</span> : <span className="dim">{l.status}</span>}
              </span>
            </div>
          ))}
        </Panel>
      )}
      {data.recentSales.length > 0 && (
        <Panel title="Recent sales">
          {data.recentSales.map((s: any, i: number) => (
            <div key={i} className="feed-item">
              <ItemIcon itemId={s.itemId} size={24} />
              <span className="grow"><PlayerLink id={s.buyerId} name={s.buyerName} /> bought <ItemName itemId={s.itemId} />{s.qty > 1 ? ` ×${s.qty}` : ''} from <PlayerLink id={s.sellerId} name={s.sellerName} /></span>
              <span className="money">{money(s.price)}</span>
              <span className="when">{ago(s.soldAt)}</span>
            </div>
          ))}
        </Panel>
      )}
      <div className="small dim">Items you’ve sold here: {fmt(me.marketSales ?? 0)}</div>
    </div>
  );
}

function SellForm({ owned, preselect, feePct, onDone }: { owned: any[]; preselect?: string; feePct: number; onDone: () => void }) {
  const { act, byId, setFlash, me } = useGame();
  const sellable = owned.filter((o) => byId.items.get(o.itemId)).sort((a, b) => byId.items.get(a.itemId).name.localeCompare(byId.items.get(b.itemId).name));
  const [itemId, setItemId] = useState(preselect && sellable.some((o) => o.itemId === preselect) ? preselect : '');
  const [qty, setQty] = useState('1');
  const [kind, setKind] = useState<'fixed' | 'auction'>('fixed');
  const [price, setPrice] = useState('');
  const [hours, setHours] = useState('24');
  const [guide, setGuide] = useState<any>(null);
  const [open, setOpen] = useState(!!preselect);
  useEffect(() => {
    setGuide(null);
    if (itemId) api.get(`/api/market/price?item=${encodeURIComponent(itemId)}`).then(setGuide).catch(() => {});
  }, [itemId]);
  if (!me.isHuman) return null;
  if (!open) return <div className="right" style={{ marginBottom: 6 }}><button className="btn green" onClick={() => setOpen(true)}>Sell an item</button></div>;
  const have = sellable.find((o) => o.itemId === itemId)?.qty ?? 0;
  const priceNum = Math.floor(Number(price.replace(/[^0-9]/g, '')));
  const submit = async () => {
    const r = await act('/api/market/list', { itemId, qty: Number(qty), kind, price: priceNum, hours: Number(hours) });
    if (r) {
      setFlash({ kind: 'good', text: <>Your <ItemName itemId={itemId} /> is on the market.</> });
      setPrice('');
      setOpen(false);
      onDone();
    }
  };
  return (
    <Panel title="Sell an item" right={<a className="small" onClick={() => setOpen(false)}>close</a>}>
      <div className="form-grid">
        <label>Item</label>
        <div className="row" style={{ alignItems: 'center', gap: 6 }}>
          {itemId && <ItemIcon itemId={itemId} size={32} />}
          <select value={itemId} onChange={(e) => setItemId(e.target.value)} className="grow">
            <option value="">Choose from your inventory…</option>
            {sellable.map((o) => <option key={o.itemId} value={o.itemId}>{byId.items.get(o.itemId).name} (you have {o.qty})</option>)}
          </select>
        </div>
        <label>How many</label>
        <input value={qty} onChange={(e) => setQty(e.target.value)} style={{ width: 70 }} />
        <label>Type</label>
        <div>
          <label className="radio"><input type="radio" checked={kind === 'fixed'} onChange={() => setKind('fixed')} /> Buy Now</label>{' '}
          <label className="radio"><input type="radio" checked={kind === 'auction'} onChange={() => setKind('auction')} /> Auction</label>
          {kind === 'auction' && (
            <select value={hours} onChange={(e) => setHours(e.target.value)} style={{ marginLeft: 6 }}>
              {[1, 4, 12, 24, 48].map((h) => <option key={h} value={h}>{h} hour{h === 1 ? '' : 's'}</option>)}
            </select>
          )}
        </div>
        <label>{kind === 'auction' ? 'Starting bid' : 'Price'}</label>
        <div>
          $ <input value={price} placeholder="amount" onChange={(e) => setPrice(e.target.value)} style={{ width: 110 }} />
          {priceNum > 0 && <span className="small dim"> · you’d get {money(priceNum - Math.floor((priceNum * feePct) / 100))} after the fee</span>}
          {guide?.averageUnitPrice && <div className="small dim">Recent sales: about {money(guide.averageUnitPrice)} each ({guide.sales} sale{guide.sales === 1 ? '' : 's'})</div>}
        </div>
      </div>
      <div className="right"><button className={`btn ${itemId && priceNum > 0 && Number(qty) >= 1 && Number(qty) <= have ? 'green' : 'disabled'}`} onClick={submit}>List it</button></div>
    </Panel>
  );
}
